import { Collateral, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { noop } from 'lodash'
import React from 'react'
import { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { createCollateralDetailsByIdGetterSelector } from 'selectors/collateralSelectors'
import { DatePickerField, DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import { ReadonlyFields } from './CollateralDialogComponent'
import { CollateralDialogFields } from './collateralDialogUtils'

interface CollateralAdditionalDetailsPanelProps {
  collateral: Collateral
  readOnly: ReadonlyFields
  validationIssues: ValidationIssue[]
}

export const CollateralAdditionalDetailsPanel: React.FC<CollateralAdditionalDetailsPanelProps> = props => {
  const { collateral, readOnly, validationIssues } = props
  const getCollateralDetailsById = useSelector(createCollateralDetailsByIdGetterSelector)
  const collateralDetails = getCollateralDetailsById(collateral.id)

  if (!collateralDetails) {
    return null
  }

  return (
    <Fragment>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-AssetStatus'}
            fieldName={CollateralDialogFields.ASSET_STATUS}
            label={Locale.translate(Translation.TC_ASSET_STATUS)}
            value={collateral.assetStatus}
            readOnly={readOnly.assetStatus}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralAdditionalDetailsPanel-AssetStatusEffectiveDate'}
            fieldName={CollateralDialogFields.ASSET_STATUS_EFFECTIVE_DATE}
            label={Locale.translate(Translation.TC_ASSET_STATUS_EFFECTIVE_DATE)}
            value={collateral.assetStatusEffectiveDate}
            readOnly={readOnly.assetStatusEffectiveDate}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-BondType'}
            fieldName={CollateralDialogFields.BOND_TYPE}
            label={Locale.translate(Translation.TC_BOND_TYPE)}
            value={collateralDetails.bondType}
            readOnly={readOnly.bondType}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CallableFlag'}
            fieldName={CollateralDialogFields.CALLABLE_FLAG}
            label={Locale.translate(Translation.TC_CALLABLE_FLAG)}
            value={collateralDetails.callableFlag}
            readOnly={readOnly.callableFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfRiskAll'}
            fieldName={CollateralDialogFields.COUNTRY_OF_RISK_ALL}
            label={Locale.translate(Translation.TC_COUNTRY_OF_RISK_ALL)}
            value={collateral.countryOfRiskAll}
            readOnly={readOnly.countryOfRiskAll}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfRiskPrimary'}
            fieldName={CollateralDialogFields.COUNTRY_OF_RISK_PRIMARY}
            label={Locale.translate(Translation.TC_COUNTRY_OF_RISK_PRIMARY)}
            value={collateral.countryOfRiskPrimary}
            readOnly={readOnly.countryOfRiskPrimary}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfRiskUltimateParentAll'}
            fieldName={CollateralDialogFields.COUNTRY_OF_RISK_ULTIMATE_PARENT_ALL}
            label={Locale.translate(Translation.TC_COUNTRY_OF_RISK_ULTIMATE_PARENT_ALL)}
            value={collateral.countryOfRiskUltimateParentAll}
            readOnly={readOnly.countryOfRiskUltimateParentAll}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfRiskUltimateParentPrimary'}
            fieldName={CollateralDialogFields.COUNTRY_OF_RISK_ULTIMATE_PARENT_PRIMARY}
            label={Locale.translate(Translation.TC_COUNTRY_OF_RISK_ULTIMATE_PARENT_PRIMARY)}
            value={collateral.countryOfRiskUltimateParentPrimary}
            readOnly={readOnly.countryOfRiskUltimateParentPrimary}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralAdditionalDetailsPanel-CouponRateEffectiveDate'}
            fieldName={CollateralDialogFields.COUPON_RATE_EFFECTIVE_DATE}
            label={Locale.translate(Translation.TC_COUPON_RATE_EFFECTIVE_DATE)}
            value={collateralDetails.couponRateEffectiveDate}
            readOnly={readOnly.couponRateEffectiveDate}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-Domicile'}
            fieldName={CollateralDialogFields.DOMICILE}
            label={Locale.translate(Translation.TC_DOMICILE)}
            value={collateral.domicile}
            readOnly={readOnly.domicile}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-ECBIndicator'}
            fieldName={CollateralDialogFields.ECB_INDICATOR}
            label={Locale.translate(Translation.TC_ECB_INDICATOR)}
            value={collateralDetails.ecbIndicator}
            readOnly={readOnly.ecbIndicator}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-ExDividendCalendar'}
            fieldName={CollateralDialogFields.EX_DIVIDEND_CALENDAR}
            label={Locale.translate(Translation.TC_EX_DIVIDEND_CALENDAR)}
            value={collateralDetails.exDividendCalendar}
            readOnly={readOnly.exDividendCalendar}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-ExDividendRule'}
            fieldName={CollateralDialogFields.EX_DIVIDEND_RULE}
            label={Locale.translate(Translation.TC_EX_DIVIDEND_RULE)}
            value={collateralDetails.exDividendRule}
            readOnly={readOnly.exDividendRule}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-ExDividendValue'}
            fieldName={CollateralDialogFields.EX_DIVIDEND_VALUE}
            label={Locale.translate(Translation.TC_EX_DIVIDEND_VALUE)}
            value={collateralDetails.exDividendValue}
            readOnly={readOnly.exDividendValue}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralAdditionalDetailsPanel-MicReportDate'}
            fieldName={CollateralDialogFields.MIC_REPORT_DATE}
            label={Locale.translate(Translation.TC_MIC_REPORT_DATE)}
            value={collateral.micReportDate}
            readOnly={readOnly.micReportDate}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-FitchRating'}
            fieldName={CollateralDialogFields.FITCH_RATING}
            label={Locale.translate(Translation.TC_FITCH_RATING)}
            value={collateralDetails.fitchRating}
            readOnly={readOnly.fitchRating}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-FloatIndexType'}
            fieldName={CollateralDialogFields.FLOATING_INDEX_TYPE}
            label={Locale.translate(Translation.TC_FLOAT_INDEX_TYPE)}
            value={collateralDetails.floatIndexType}
            readOnly={readOnly.floatIndexType}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-Series'}
            fieldName={CollateralDialogFields.SERIES}
            label={Locale.translate(Translation.TC_SERIES)}
            value={collateralDetails.series}
            readOnly={readOnly.series}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralAdditionalDetailsPanel-FloatMultiplier'}
            fieldName={CollateralDialogFields.FLOAT_MULTIPLIER}
            label={Locale.translate(Translation.TC_FLOAT_MULTIPLIER)}
            value={collateralDetails.floatMultiplier}
            readOnly={readOnly.floatMultiplier}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralAdditionalDetailsPanel-FloatOffset'}
            fieldName={CollateralDialogFields.FLOAT_OFFSET}
            label={Locale.translate(Translation.TC_FLOAT_OFFSET)}
            value={collateralDetails.floatOffset}
            readOnly={readOnly.floatOffset}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-FloaterFlag'}
            fieldName={CollateralDialogFields.FLOATER_FLAG}
            label={Locale.translate(Translation.TC_FLOATER_FLAG)}
            value={collateralDetails.floaterFlag}
            readOnly={readOnly.floaterFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralAdditionalDetailsPanel-FloatingRateFixingDate'}
            fieldName={CollateralDialogFields.FLOATING_RATE_FIXING_DATE}
            label={Locale.translate(Translation.TC_FLOATING_RATE_FIXING_DATE)}
            value={collateralDetails.floatingRateFixingDate}
            readOnly={readOnly.floatingRateFixingDate}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-IndustrySectorCode'}
            fieldName={CollateralDialogFields.INDUSTRY_SECTOR_CODE}
            label={Locale.translate(Translation.TC_INDUSTRY_SECTOR_CODE)}
            value={collateralDetails.industrySectorCode}
            readOnly={readOnly.industrySectorCode}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-IndustrySubSectorCode'}
            fieldName={CollateralDialogFields.INDUSTRY_SUB_SECTOR_CODE}
            label={Locale.translate(Translation.TC_INDUSTRY_SUB_SECTOR_CODE)}
            value={collateralDetails.industrySubSectorCode}
            readOnly={readOnly.industrySubSectorCode}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-IsSecuredFlag'}
            fieldName={CollateralDialogFields.IS_SECURED_FLAG}
            label={Locale.translate(Translation.TC_IS_SECURED_FLAG)}
            value={collateralDetails.isSecuredFlag}
            readOnly={readOnly.isSecuredFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralAdditionalDetailsPanel-Lookback'}
            fieldName={CollateralDialogFields.LOOK_BACK}
            label={Locale.translate(Translation.TC_LOOK_BACK)}
            value={collateralDetails.lookBack}
            readOnly={readOnly.lookBack}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-SAndPRating'}
            fieldName={CollateralDialogFields.S_AND_PR_RATING}
            label={Locale.translate(Translation.TC_S_AND_P_RATING)}
            value={collateralDetails.sandPRating}
            readOnly={readOnly.sandPRating}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-MifidIndexIdentifier'}
            fieldName={CollateralDialogFields.MIFID_INDEX_IDENTIFIER}
            label={Locale.translate(Translation.TC_MIFID_INDEX_IDENTIFIER)}
            value={collateralDetails.mifidIndexIdentifier}
            readOnly={readOnly.mifidIndexIdentifier}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-MifidIndexName'}
            fieldName={CollateralDialogFields.MIFID_INDEX_NAME}
            label={Locale.translate(Translation.TC_MIFID_INDEX_NAME)}
            value={collateralDetails.mifidIndexName}
            readOnly={readOnly.mifidIndexName}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-MifidIndexTerm'}
            fieldName={CollateralDialogFields.MIFID_INDEX_TERM}
            label={Locale.translate(Translation.TC_MIFID_INDEX_TERM)}
            value={collateralDetails.mifidIndexTerm}
            readOnly={readOnly.mifidIndexTerm}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-MoodysRating'}
            fieldName={CollateralDialogFields.MOODYS_RATING}
            label={Locale.translate(Translation.TC_MOODYS_RATING)}
            value={collateralDetails.moodysRating}
            readOnly={readOnly.moodysRating}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-MultiStepFloatFlag'}
            fieldName={CollateralDialogFields.MULTI_STEP_FLOAT_FLAG}
            label={Locale.translate(Translation.TC_MULTI_STEP_FLOAT_FLAG)}
            value={collateralDetails.multiStepFloatFlag}
            readOnly={readOnly.multiStepFloatFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralAdditionalDetailsPanel-OfferingType'}
            fieldName={CollateralDialogFields.OFFERING_TYPE}
            label={Locale.translate(Translation.TC_OFFERING_TYPE)}
            value={collateralDetails.offeringType}
            readOnly={readOnly.offeringType}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-PerpetualSecurityFlag'}
            fieldName={CollateralDialogFields.PERPETUAL_SECURITY_FLAG}
            label={Locale.translate(Translation.TC_PERPETUAL_SECURITY_FLAG)}
            value={collateralDetails.perpetualSecurityFlag}
            readOnly={readOnly.perpetualSecurityFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-PrivatePlacementFlag'}
            fieldName={CollateralDialogFields.PRIVATE_PLACEMENT_FLAG}
            label={Locale.translate(Translation.TC_PRIVATE_PLACEMENT_FLAG)}
            value={collateralDetails.privatePlacementFlag}
            readOnly={readOnly.privatePlacementFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-RefinitivClassificationScheme'}
            fieldName={CollateralDialogFields.REFINITIV_CLASSIFICATION_SCHEME}
            label={Locale.translate(Translation.TC_REFINITIV_CLASSIFICATION_SCHEME)}
            value={collateralDetails.refinitivClassificationScheme}
            readOnly={readOnly.refinitivClassificationScheme}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-SettlementDateConvention'}
            fieldName={CollateralDialogFields.SETTLEMENT_DATE_CONVENTION}
            label={Locale.translate(Translation.TC_SETTLEMENT_DATE_CONVENTION)}
            value={collateralDetails.settlementDateConvention}
            readOnly={readOnly.settlementDateConvention}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-SinkableFlag'}
            fieldName={CollateralDialogFields.SINKABLE_FLAG}
            label={Locale.translate(Translation.TC_SINKABLE_FLAG)}
            value={collateralDetails.sinkableFlag}
            readOnly={readOnly.sinkableFlag}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralAdditionalDetailsPanel-TotalAmountOutstanding'}
            fieldName={CollateralDialogFields.TOTAL_AMOUNT_OUTSTANDING}
            label={Locale.translate(Translation.TC_TOTAL_AMOUNT_OUTSTANDING)}
            value={collateralDetails.totalAmountOutstanding}
            readOnly={readOnly.totalAmountOutstanding}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
    </Fragment>
  )
}
